import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import {
  DEFAULT_LOCALE,
  LocaleConfig,
  SUPPORTED_LOCALES,
  currentLocale,
  isLocaleSupported,
  setLocale,
} from '../state/locale.state';

export type { LocaleConfig };

/**
 * Service for managing application language and locale
 * Handles runtime language switching with ngx-translate (no page reload!)
 */
@Injectable({ providedIn: 'root' })
export class LanguageService {
  private readonly translateService = inject(TranslateService);

  readonly currentLanguage = currentLocale;

  constructor() {
    this.translateService.setDefaultLang(DEFAULT_LOCALE);
    this.applyLanguage(currentLocale());
  }

  getLanguage(): string {
    return this.currentLanguage();
  }

  getSupportedLanguages(): LocaleConfig[] {
    return SUPPORTED_LOCALES;
  }

  getLocaleConfig(code: string): LocaleConfig | undefined {
    return SUPPORTED_LOCALES.find((locale) => locale.code === code);
  }

  isLocaleSupported(code: string): boolean {
    return isLocaleSupported(code);
  }

  getStoredLocale(): string {
    return currentLocale();
  }

  /**
   * Switch language instantly - no page reload needed!
   *
   * @param locale - Target locale code (e.g., 'nb', 'en')
   */
  switchLanguage(locale: string): void {
    if (!isLocaleSupported(locale)) {
      console.error(`Locale ${locale} is not supported`);
      return;
    }

    if (locale === this.currentLanguage()) {
      return;
    }

    // Locale first: a failed i18n load must not leave signal and storage disagreeing.
    setLocale(locale);
    this.applyLanguage(locale);
  }

  getCurrentLanguageName(): string {
    const config = this.getLocaleConfig(this.currentLanguage());
    return config?.nativeName ?? this.currentLanguage();
  }

  instant(key: string, params?: object): string {
    return this.translateService.instant(key, params);
  }

  get(key: string, params?: object) {
    return this.translateService.get(key, params);
  }

  // Bypasses switchLanguage's same-locale early return, which would otherwise skip
  // the initial use() when the stored locale already matches the signal.
  private applyLanguage(locale: string): void {
    this.translateService.use(locale).subscribe();
  }
}
